import {
  capitalize,
  excludeValueFromCollection,
  generateOptions,
  getMinDate,
  periodRenderer,
  personRenderer,
  sortCollection
} from '@/lib/generic.utils'
import { cloneDeep } from 'lodash'
import Vue from 'vue'
// import Lang from 'lang.js'
import { mapState } from 'vuex'
// const messages = {}
// const lang = new Lang({ messages })
import {
  convertToNumber,
  formatPrice,
  formatInputNumber,
  formatDateForPost,
  formatDateForLocale,
  formatDateForCustomDisplay,
  formatDateTimeForLocale
} from '../lib/formatting.utils'
import moment from 'moment'

const GLOBAL_MIXIN = Vue.mixin({
  filters: {
    capitalize: value => {
      if (!value) return ''
      value = value.toString()
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },
  computed: {
    ...mapState({
      settings: state => state.settings,
      lmLocale: state => state.settings.locale,
      currencies: state => state.settings.options.currencies,
      decimalSeparator: state => state.settings.decimal_separator,
      thousandsSeparator: state => state.settings.thousand_separator
    }),
    globalVarsAndFns() {
      const userDateFormat = this.settings.date_format
      const BEdateFormat = 'YYYY-MM-DD'
      const formatDate = d => moment(d, BEdateFormat).format(userDateFormat)
      const getDateAfterOneYear = d =>
        moment(d, userDateFormat)
          .add(1, 'year')
          .subtract(1, 'day')
          .format(userDateFormat)
      return {
        userDateFormat,
        BEdateFormat,
        formatDate,
        getDateAfterOneYear
      }
    },

    emailPreferences() {
      return this.settings.options.email_preferences.map(item => ({
        ...item,
        label: capitalize(item.name.replace('_', ' '))
      }))
    }
  },
  methods: {
    getSetting(key, defaultValue) {
      if (!this.settings) {
        return
      }
      let settings = cloneDeep(this.settings)

      if (key === null) {
        return settings
      }

      if (typeof settings[key] !== 'undefined') {
        return settings[key]
      }

      if (key.indexOf('.') === -1) {
        return settings[key] || defaultValue
      }

      let keys = key.split('.')

      for (let i = 0; i < keys.length; i++) {
        if (typeof settings[keys[i]] !== 'undefined') {
          settings = settings[keys[i]]
        } else {
          return defaultValue
        }
      }

      return settings
    },

    convertToNumber(value, decimals = 2) {
      return convertToNumber(value, decimals)
    },

    formatPrice(value, decimals = 2, inCell = false, locale) {
      if (!locale) {
        locale = this.getSetting('locale')
      }
      let decimalSeparator = this.settings.decimal_separator
      let thousandsSeparator = this.settings.thousand_separator

      return formatPrice(value, decimals, inCell, locale, decimalSeparator, thousandsSeparator)
    },

    formatDateForPost(date, customFormat) {
      if (!date) {
        return null
      }
      // format date for post request
      return formatDateForPost(date, customFormat)
    },

    formatDateForLocale(date) {
      return formatDateForLocale(date)
    },
    formatDateForCustomDisplay(date, format) {
      return formatDateForCustomDisplay(date, format)
    },
    formatDateTimeForLocale(date, locale) {
      return formatDateTimeForLocale(date, locale)
    },
    getTranslation() {
      return ''
    },
    /**
     * Prepares options for q-select and any other components using [{label, value}] options; keys for resulting
     * object are configurable; provides sensible defaults - in most cases providing the collection will suffice
     * @param {Object} parameters - a configuration object containing options for
     * processing and sorting
     * @param {Array} parameters.collection - data to generate options from
     * @param {Boolean} [parameters.valueAsObject = false]  - the value for the option should be the whole object
     * @param {String} [parameters.labelKey = name] -  property on the object where we take the label text from
     * @param {String} [parameters.combineLabelWith = null] -  property on the object where we take a value to
     * append to the label
     * @param {String} [parameters.concatSep = space] -  separator of concat string for label
     * @param {String} [parameters.valueKey = id] - property on the object that will serve as the selection value
     * @param {String} [parameters.resultingLabelKey = label] - the key the display value will have
     * @param {String} [parameters.resultingValueKey = value] - the key the value will have
     * @param {Object|String|Number} [parameters.excludeValue = null] - the type of this should match the value we
     * want to exclude and contain sufficient information to isolate the item in the collection; it excludes based
     * on equality with `valueKey`
     * @param {Boolean} [parameters.includeNone = false] - if true, it will add a {value: null, label: 'none'} item
     * to options list
     * @param {Boolean} [parameters.capitalizeOptionLabel = false] - if true, it will capitalize the label string
     * @param {Boolean} [parameters.sort = true] - if true, it will sort the options list after processing,
     * based on provided parameters
     * @param {Boolean} [parameters.sortBefore = false] - whether the collection should be sorted before
     * processing; useful when we want to sort by other values than name / label; it will use the sort params passed in
     * @param {String} [parameters.sortingOptions.sortOrder = asc] - 'asc', 'desc' sort direction
     * @param {String} [parameters.sortingOptions.sortBy = label] - property to sort by
     * @param {String} [parameters.sortingOptions.sortDataType = string] - string, date, number
     * @returns {Array}
     */
    generateOptions(parameters) {
      return generateOptions(parameters)
    },
    /**
     * Sorts an array based on provided parameters
     * @param {Array} collection - the collection to be sorted
     * @param sortingOptions
     * @param {String} [sortingOptions.sortOrder = asc] - 'asc', 'desc' sort direction
     * @param {String} [sortingOptions.sortBy = label] - property to sort by
     * @param {String} [sortingOptions.sortDataType = string] - string, date, number
     * @returns {*}
     */
    sortCollection(collection, sortingOptions) {
      return sortCollection(collection, sortingOptions)
    },
    /**
     * Checks if a value should be excluded from the result of generateOptions(params)
     * @param {Object|String|Number} value - value to check for exclusion
     * @param {Boolean} valueAsObject - if true, the value will be treated as an object
     * @param {String} valueKey - if valueAsObject === false, value[valueKey] will be checked for exclusion
     * @param {Object|String|Number} valueToExclude - value to match against for exclusion
     * @returns {boolean} - representing the equality between value and valueToExclude - if true, the value should
     * be skipped
     */
    excludeValue(value, valueAsObject, valueKey, valueToExclude) {
      return excludeValueFromCollection(value, valueAsObject, valueKey, valueToExclude)
    },
    /**
     * Capitalized the first letter in every word of the provided text
     * @param text
     * @returns {string}
     */
    capitalize(text) {
      return capitalize(text)
    },
    formatInputNumber(numberString, outputDecimals = 2, decimalSeparator, thousandsSeparator) {
      if (!numberString) {
        return
      }
      if (!decimalSeparator) {
        decimalSeparator = this.settings.decimal_separator
      }
      if (!thousandsSeparator) {
        thousandsSeparator = this.settings.thousand_separator
      }
      thousandsSeparator = thousandsSeparator === 'space' ? ' ' : thousandsSeparator

      return formatInputNumber(numberString, outputDecimals, decimalSeparator, thousandsSeparator)
    },
    getMinDate(date) {
      return getMinDate(date)
    },
    getFileType(url) {
      return url
        .split('.')
        .pop()
        .split(/#|\?/)[0]
    },
    getPeriod(item) {
      return periodRenderer(item)
    },
    formatName(obj) {
      return personRenderer(obj)
    },
    capitalizeSnakeCase(str) {
      // some_text becomes Some Text
      return str
        .split('_')
        .map(word => word.replace(/^./, str => str.toUpperCase()))
        .join(' ')
    },
    removePriceFormat(value) {
      if (!value) return
      return value.replace(this.settings.thousand_separator, '').replace(this.settings.decimal_separator, '.')
    }
  }
})

export default GLOBAL_MIXIN
